import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth } from '../../lib/supabase'
import { showError, showSuccess } from '../../shared/components/Toast'

const AuthContext = createContext({})

// Email links land on these paths; the user must choose a password before using the app
const PASSWORD_FLOW_PATHS = {
  '/reset-password': 'reset',
  '/set-password': 'invite',
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  // 'reset' or 'invite' while the user arrived via an email link and hasn't set a password yet
  const [passwordFlow, setPasswordFlow] = useState(
    () => PASSWORD_FLOW_PATHS[window.location.pathname] ?? null
  )

  useEffect(() => {
    // Listen for auth state changes - this will handle initial session automatically
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email)
      setUser(session?.user ?? null)
      setLoading(false)

      if (event === 'PASSWORD_RECOVERY') {
        setPasswordFlow((flow) => flow ?? 'reset')
      }

      // Show success message for successful login (but not when arriving via an email link)
      if (event === 'SIGNED_IN' && session?.user && !PASSWORD_FLOW_PATHS[window.location.pathname]) {
        showSuccess(`Welcome back, ${session.user.email}!`)
      }

      // Show message for sign out
      if (event === 'SIGNED_OUT') {
        showSuccess('You have been signed out successfully')
      }
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  // Form actions (signIn, resetPassword, updatePassword) must not toggle `loading`:
  // AuthWrapper swaps the whole screen for a spinner while it is true, which unmounts
  // the form and discards its state. Callers show their own errors inline.
  const signIn = async (email, password) => {
    try {
      const { data, error } = await auth.signIn(email, password)

      if (error) {
        return { success: false, error }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Sign in error:', error)
      return { success: false, error }
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await auth.signOut()
      
      if (error) {
        showError(error.message)
        return { success: false, error }
      }

      return { success: true }
    } catch (error) {
      console.error('Sign out error:', error)
      showError('An unexpected error occurred during sign out')
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      const { data, error } = await auth.resetPassword(email)

      if (error) {
        return { success: false, error }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Reset password error:', error)
      return { success: false, error }
    }
  }

  const updatePassword = async (password) => {
    try {
      const { data, error } = await auth.updatePassword(password)

      if (error) {
        return { success: false, error }
      }

      showSuccess(passwordFlow === 'invite' ? 'Welcome! Your password is set.' : 'Your password has been updated!')
      setPasswordFlow(null)
      return { success: true, data }
    } catch (error) {
      console.error('Update password error:', error)
      return { success: false, error }
    }
  }

  const value = {
    user,
    loading,
    passwordFlow,
    clearPasswordFlow: () => setPasswordFlow(null),
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    isAuthenticated: !!user,
    isAdmin: user?.app_metadata?.role === 'admin'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
